import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getMyCalendar, getMyDays, getLockState } from '../../api/timesheetApi';
import {
  getDelivery, getWorkItemContext, createWorkItem, readServerError,
} from '../../api/deliveryApi';
import { getMyProjects } from '../../api/myProjectsApi';
import { usePermissions } from '../PermissionsContext';
import {
  MONTH_NAMES, DOW_ABBR, DOW_FULL,
  addDays, firstOfMonth, lastOfMonth, fmtIso, isWeekend, mondayOf,
  parseIso, fmtDdMm, fmtDdMmYy, fmtHours,
} from '../Reports/calendarUtils';
import WorkItemDrawer from '../Projects/Delivery/WorkItemDrawer';
import NonBillableDrawer from './NonBillableDrawer';
import { useToast } from '../Common/ToastProvider';
import './MyTimesheetPage.css';

// MT-S1 + MT-S2 — My Timesheet redesign.
// Reference: docs/presentations/hybrid-pm-concept/my-timesheet-redesign.html
// Reads: /api/timesheet/my-calendar (Month + KPI) and
// /api/timesheet/my-days (Week rows source + Day view).
// MT-S2 adds click targets on Day entries: task title / billable cell
// opens the shared WorkItemDrawer (fed by /api/work-items/{id}/context);
// project title / NB cell opens the local NonBillableDrawer; ShortLeave
// / Leave rows and empty (·) cells are deliberately dead.

// Week helpers — the mockup + MyTimesheet week is Mon..Sun, unlike
// calendarUtils.rangeOf which is Mon..Fri for the team-calendar range.
const weekRange = (anchor) => {
  const mon = mondayOf(anchor);
  return { from: mon, to: addDays(mon, 6) };
};

const periodLabel = (view, anchor) => {
  if (view === 'm') return `${MONTH_NAMES[anchor.getMonth()]} ${anchor.getFullYear()}`;
  if (view === 'w') {
    const { from, to } = weekRange(anchor);
    return `${fmtDdMm(from)} – ${fmtDdMmYy(to)}`;
  }
  return `${DOW_FULL[anchor.getDay()]}, ${fmtDdMmYy(anchor)}`;
};

const navStep = (view, anchor, dir) => {
  if (view === 'm') return new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1);
  if (view === 'w') return addDays(anchor, 7 * dir);
  return addDays(anchor, dir);
};

const rangeOf = (view, anchor) => {
  if (view === 'm') return { from: firstOfMonth(anchor), to: lastOfMonth(anchor) };
  if (view === 'w') return weekRange(anchor);
  return { from: anchor, to: anchor };
};

const combinationClass = (day) => {
  const b = Number(day.billableHours) || 0;
  const n = Number(day.nonBillableHours) || 0;
  const l = Number(day.leaveHours) || 0;
  if (day.type === 'PaidLeave' || day.type === 'SickLeave') return 'mt-c-lv';
  const nz = (b > 0 ? 1 : 0) + (n > 0 ? 1 : 0) + (l > 0 ? 1 : 0);
  if (nz === 0) return '';
  if (nz > 1) return 'mt-c-mix';
  if (b > 0) return 'mt-c-b';
  if (n > 0) return 'mt-c-nb';
  return 'mt-c-lv';
};

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

// KPI band (mockup §KPI). Utilization = billable / (workingDays × 8),
// clamped visually at 100% but the number can go past. `gaps` counts
// past working days with zero total; `shorts` sums short-absence hours.
const buildKpis = (monthData, today) => {
  const days = monthData?.consultant?.days || [];
  let b = 0; let n = 0; let lv = 0; let wd = 0; let gaps = 0; let shorts = 0;
  days.forEach((d) => {
    const dt = parseIso(d.date);
    const we = isWeekend(dt) || d.type === 'Weekend';
    if (we || d.type === 'Holiday') return;
    wd += 1;
    if (dt > today) return;
    const bb = Number(d.billableHours) || 0;
    const nn = Number(d.nonBillableHours) || 0;
    const ll = Number(d.leaveHours) || 0;
    b += bb; n += nn; lv += ll;
    // shortLeaveHours is not directly on the range DTO; treat leaveHours
    // ≠ 8 on a PaidLeave/SickLeave day as short. Simple heuristic: on a
    // non-Leave day, all leaveHours are short-absence.
    if (d.type !== 'PaidLeave' && d.type !== 'SickLeave') shorts += ll;
    if (bb + nn + ll === 0) gaps += 1;
  });
  const denom = wd * 8;
  const util = denom > 0 ? Math.round((b / denom) * 1000) / 10 : 0;
  return { b, n, lv, wd, gaps, util, shorts };
};

export default function MyTimesheetPage() {
  const { hasPermission, loading: permLoading } = usePermissions();
  const toast = useToast();
  const [view, setView] = useState('m');
  const [anchor, setAnchor] = useState(() => new Date());
  // Month-KPI + Month view share the same DTO; Week view uses month
  // for KPI and the week slice of it for the matrix; Day view uses
  // /my-days for the entry rows.
  const [monthData, setMonthData] = useState(null);
  const [daysData, setDaysData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lockState, setLockState] = useState(null);

  // MT-S2 — drawer state.
  // Billable drawer (WorkItemDrawer, reused from delivery): opened
  // with a full context payload the drawer's props expect.
  const [wiCtx, setWiCtx] = useState(null);
  const [wiLoadingId, setWiLoadingId] = useState(null);
  // Non-billable drawer state (local component): opened with a
  // project id + prior hours/note.
  const [nbState, setNbState] = useState(null);
  // "+ Add billable task" drawer (project → sold theme → title).
  const [addBillState, setAddBillState] = useState(null);

  const range = useMemo(() => rangeOf(view, anchor), [view, anchor]);
  const monthRange = useMemo(() => ({
    from: firstOfMonth(anchor), to: lastOfMonth(anchor),
  }), [anchor]);
  const today = useMemo(() => startOfToday(), []);

  // Locked-month indicator — sourced from the existing lock state
  // endpoint. Purely informational in this slice (no writes here).
  useEffect(() => {
    let cancelled = false;
    getLockState()
      .then((s) => { if (!cancelled) setLockState(s); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  // MT-S2 — the caller's project rollup (used by both drawers' project
  // pickers). Loaded once; fine to be stale within a session.
  const [myProjects, setMyProjects] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getMyProjects()
      .then((list) => { if (!cancelled) setMyProjects(Array.isArray(list) ? list : []); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);
  const nbProjects = useMemo(
    () => myProjects.filter((p) => p.isBillable === false),
    [myProjects],
  );
  const billableProjects = useMemo(
    () => myProjects.filter((p) => p.isBillable !== false),
    [myProjects],
  );

  const load = useCallback(async () => {
    // TC-S3.1 stale-while-revalidate: ◀/▶ keeps the current visual;
    // full spinner only when the buffer for the current view is empty.
    const havePrev = view === 'm'
      ? monthData != null
      : view === 'w'
        ? monthData != null
        : daysData != null;
    if (havePrev) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      if (view === 'd') {
        const dto = await getMyDays(fmtIso(range.from), fmtIso(range.to));
        setDaysData(dto);
        // Month KPI still needed → separate fetch (kept warm across days).
        if (monthData == null || monthData._month !== `${anchor.getFullYear()}-${anchor.getMonth()}`) {
          const m = await getMyCalendar(fmtIso(monthRange.from), fmtIso(monthRange.to));
          m._month = `${anchor.getFullYear()}-${anchor.getMonth()}`;
          setMonthData(m);
        }
      } else if (view === 'w') {
        const dto = await getMyDays(fmtIso(range.from), fmtIso(range.to));
        setDaysData(dto);
        if (monthData == null || monthData._month !== `${anchor.getFullYear()}-${anchor.getMonth()}`) {
          const m = await getMyCalendar(fmtIso(monthRange.from), fmtIso(monthRange.to));
          m._month = `${anchor.getFullYear()}-${anchor.getMonth()}`;
          setMonthData(m);
        }
      } else {
        const dto = await getMyCalendar(fmtIso(range.from), fmtIso(range.to));
        dto._month = `${anchor.getFullYear()}-${anchor.getMonth()}`;
        setMonthData(dto);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : 'Failed to load timesheet data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, range.from, range.to, monthRange.from, monthRange.to, anchor]);

  useEffect(() => { load(); }, [load]);

  // ─── MT-S2 callbacks (declared above the early-return so hook
  // ordering is stable across renders). ────────────────────────────
  const reload = useCallback(async (dateIso) => {
    try {
      const [d, m] = await Promise.all([
        getMyDays(dateIso ?? fmtIso(anchor), dateIso ?? fmtIso(anchor)),
        getMyCalendar(fmtIso(monthRange.from), fmtIso(monthRange.to)),
      ]);
      setDaysData(d);
      m._month = `${anchor.getFullYear()}-${anchor.getMonth()}`;
      setMonthData(m);
    } catch (e) { /* non-fatal */ }
  }, [anchor, monthRange.from, monthRange.to]);

  const openWiDrawer = useCallback(async (workItemId) => {
    if (!workItemId) return;
    setWiLoadingId(workItemId);
    try {
      const dto = await getWorkItemContext(workItemId);
      setWiCtx(dto);
    } catch (e) {
      toast?.(readServerError(e), { variant: 'error' });
    } finally {
      setWiLoadingId(null);
    }
  }, [toast]);

  const openNbDrawer = useCallback((row, projectsForDay, isoDate, locked) => {
    const projects = projectsForDay;
    const chosen = row?.projectName
      ? projects.find((p) => p.name === row.projectName)
      : projects[0];
    setNbState({
      date: isoDate,
      projects,
      initialProjectId: chosen?.id ?? projects[0]?.id ?? 0,
      initialHours: row?.hours ?? 0,
      initialNote: row?.note ?? '',
      locked,
    });
  }, []);

  // ─── Lock-badge for the current period's month ────────────────────
  const monthLocked = useMemo(() => {
    if (!lockState) return false;
    const y = anchor.getFullYear();
    const m = anchor.getMonth() + 1;
    if (lockState.previousMonth?.year === y && lockState.previousMonth?.month === m) {
      return !!lockState.previousMonth.isLocked;
    }
    if (lockState.currentMonth?.year === y && lockState.currentMonth?.month === m) {
      return !!lockState.currentMonth.isLocked;
    }
    return false;
  }, [lockState, anchor]);

  if (permLoading) return <div className="mt-page">Loading permissions…</div>;
  if (!hasPermission || !hasPermission('Timesheet.ViewOwnTimesheet')) {
    return <div className="mt-page">You don't have access to My Timesheet.</div>;
  }

  const goToday = () => setAnchor(new Date());
  const nav = (dir) => setAnchor((a) => navStep(view, a, dir));
  const drillToDay = (isoDate) => {
    setView('d');
    setAnchor(parseIso(isoDate));
  };
  const switchView = (v) => {
    if (view === v) return;
    // Force full spinner on a genuine view switch (different table).
    if (v === 'm') { setDaysData(null); }
    else if (v === 'd') { /* keep monthData for KPI */ }
    setView(v);
  };

  // ─── KPI band ─────────────────────────────────────────────────────
  const kpi = buildKpis(monthData, today);
  const uCls = kpi.util >= 75 ? 'green' : kpi.util < 50 ? 'red' : '';
  const barCls = kpi.util >= 75 ? 'ok' : kpi.util >= 50 ? '' : 'warn';
  const barPct = Math.max(0, Math.min(kpi.util, 100));
  const kpiBand = (
    <div className="mt-kpis" data-testid="mt-kpis">
      <div className="mt-kpi mt-hero">
        <div className="mt-l">{MONTH_NAMES[anchor.getMonth()]} {anchor.getFullYear()} — Utilization</div>
        <div className={`mt-v ${uCls}`} data-testid="mt-util-value">
          {String(kpi.util).replace('.', ',')}%
        </div>
        <div className="mt-ubar">
          <i className={barCls} style={{ width: `${barPct}%` }} />
          <span className="mt-tgt" style={{ left: '75%' }} />
          <span className="mt-tgl" style={{ left: '75%' }}>75%</span>
        </div>
      </div>
      <div className="mt-kpi">
        <div className="mt-l">Billable</div>
        <div className="mt-v">{fmtHours(kpi.b)}<span className="mt-u"> h</span></div>
      </div>
      <div className="mt-kpi">
        <div className="mt-l">Non-billable</div>
        <div className="mt-v">{fmtHours(kpi.n)}<span className="mt-u"> h</span></div>
      </div>
      <div className="mt-kpi">
        <div className="mt-l">Leave / short leave</div>
        <div className="mt-v">{fmtHours(kpi.lv)}<span className="mt-u"> h</span></div>
        <div className="mt-s">{fmtHours(kpi.shorts)} h short absences</div>
      </div>
      <div className="mt-kpi">
        <div className="mt-l">Days without logging</div>
        <div className={`mt-v ${kpi.gaps === 0 ? 'green' : 'red'}`} data-testid="mt-gaps">{kpi.gaps}</div>
        <div className="mt-s">working days so far</div>
      </div>
    </div>
  );

  // ─── Month view ───────────────────────────────────────────────────
  const renderMonth = () => {
    const days = monthData?.consultant?.days || [];
    if (days.length === 0) return null;
    const first = parseIso(days[0].date);
    const lead = (first.getDay() + 6) % 7;
    const cells = Array.from({ length: lead }).map(() => null).concat(days);
    while (cells.length % 7 !== 0) cells.push(null);
    const rows = [];
    for (let r = 0; r < cells.length / 7; r++) rows.push(cells.slice(r * 7, r * 7 + 7));
    return (
      <>
        <table className="mt-cal" data-testid="mt-cal">
          <thead>
            <tr>{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((x) => <th key={x}>{x}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((week, ri) => (
              <tr key={ri}>
                {week.map((day, ci) => {
                  if (!day) return <td key={ci} className="mt-oth" />;
                  const d = parseIso(day.date);
                  const b = Number(day.billableHours) || 0;
                  const nn = Number(day.nonBillableHours) || 0;
                  const l = Number(day.leaveHours) || 0;
                  const sum = b + nn + l;
                  const we = isWeekend(d) || day.type === 'Weekend';
                  const future = d > today;
                  const gap = !we && day.type !== 'Holiday' && !future && sum === 0;
                  const isTdy = fmtIso(d) === fmtIso(today);
                  const cls = combinationClass(day);
                  const parts = [];
                  if (b > 0) parts.push(`${fmtHours(b)} bil.`);
                  if (nn > 0) parts.push(`${fmtHours(nn)} n-bil.`);
                  if (l > 0) parts.push(day.type === 'PaidLeave' || day.type === 'SickLeave' ? 'leave' : `${fmtHours(l)} leave`);
                  return (
                    <td key={day.date} className={we ? 'mt-oth' : ''}>
                      <span
                        className={`mt-dc ${we ? '' : cls}`}
                        data-testid={`mt-day-${day.date}`}
                        onClick={we ? undefined : () => drillToDay(day.date)}
                        style={we ? { cursor: 'default' } : undefined}
                      >
                        <span className="mt-dn">
                          {isTdy ? <span className="mt-tdy">{d.getDate()}</span> : d.getDate()}
                        </span>
                        {!we && (
                          <>
                            <div className="mt-dh">
                              {sum > 0
                                ? fmtHours(sum)
                                : future
                                  ? '·'
                                  : gap
                                    ? <span className="mt-gap">!</span>
                                    : '·'}
                            </div>
                            {parts.length > 0 && <div className="mt-dt">{parts.join(' + ')}</div>}
                          </>
                        )}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-legend">
          Colours:
          <i style={{ background: '#dbe9f9' }} />billable only
          <i style={{ background: '#e7ddf3' }} />combination
          <i style={{ background: '#fff3c2' }} />non-billable only
          <i style={{ background: '#e2e2e2' }} />leave
          <i style={{ background: '#fff' }} /><span style={{ color: '#c0392b', fontWeight: 700 }}>!</span> working day without logging · click a day → its detail
        </div>
      </>
    );
  };

  // ─── Week view ────────────────────────────────────────────────────
  // Rows = 7 days × columns Billable | Non-billable | Leave/sick | Total.
  // Values come from /my-days (per-day totals split by kind).
  const daySumsFromEntries = (day) => {
    let b = 0; let nn = 0; let lv = 0;
    (day.entries || []).forEach((e) => {
      if (e.kind === 'Task') { if (e.isBillable) b += Number(e.hours) || 0; else nn += Number(e.hours) || 0; }
      else if (e.kind === 'Project') { if (e.isBillable) b += Number(e.hours) || 0; else nn += Number(e.hours) || 0; }
      else { lv += Number(e.hours) || 0; } // ShortLeave + Leave
    });
    return { b, nn, lv, tot: b + nn + lv };
  };

  const renderWeek = () => {
    const days = daysData?.days || [];
    if (days.length === 0) return null;
    const T = { b: 0, nn: 0, lv: 0 };
    return (
      <table className="mt-g" data-testid="mt-week">
        <colgroup>
          <col style={{ width: 210 }} />
          <col />
          <col />
          <col />
          <col style={{ width: 84 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="mt-l">Day</th>
            <th>Billable</th>
            <th>Non-billable</th>
            <th>Leave / sick</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {days.map((day) => {
            const d = parseIso(day.date);
            const we = isWeekend(d) || day.type === 'Weekend';
            const future = d > today;
            const isTdy = fmtIso(d) === fmtIso(today);
            const s = daySumsFromEntries(day);
            T.b += s.b; T.nn += s.nn; T.lv += s.lv;
            const gap = !we && day.type !== 'Holiday' && !future && s.tot === 0;
            const cell = (v, cls) => (
              <td className={we ? 'mt-we' : ''}>
                <span
                  className={`mt-cell mt-click ${v ? cls : ''}`}
                  onClick={() => drillToDay(day.date)}
                >{we ? '·' : (v ? fmtHours(v) : '·')}</span>
              </td>
            );
            return (
              <tr key={day.date} data-testid={`mt-week-${day.date}`}>
                <td className={`mt-name${we ? ' mt-we' : ''}`} style={isTdy ? { fontWeight: 700 } : undefined}>
                  {DOW_ABBR[d.getDay()]} {fmtDdMm(d)}{isTdy ? ' · today' : ''}
                </td>
                {cell(s.b, 'mt-c-b')}
                {cell(s.nn, 'mt-c-nb')}
                {cell(s.lv, 'mt-c-lv')}
                <td className={we ? 'mt-we' : ''}>
                  <span
                    className={`mt-cell mt-click${gap ? ' mt-gap' : ''}`}
                    data-testid={`mt-week-total-${day.date}`}
                    onClick={() => drillToDay(day.date)}
                  ><b>{we ? '·' : (s.tot ? fmtHours(s.tot) : (future ? '·' : '!'))}</b></span>
                </td>
              </tr>
            );
          })}
          <tr className="mt-sum">
            <td className="mt-name">Week total</td>
            <td><span className="mt-cell">{fmtHours(T.b)}</span></td>
            <td><span className="mt-cell">{fmtHours(T.nn)}</span></td>
            <td><span className="mt-cell">{fmtHours(T.lv)}</span></td>
            <td><span className="mt-cell"><b>{fmtHours(T.b + T.nn + T.lv)}</b></span></td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderDay = () => {
    const day = (daysData?.days || [])[0];
    if (!day) return null;
    const d = parseIso(day.date);
    const we = isWeekend(d) || day.type === 'Weekend';
    const future = d > today;
    const s = daySumsFromEntries(day);
    const rows = day.entries || [];
    // MT-S3.2 — the NB drawer's project list is the caller's
    // authoritative getMyProjects rollup (now UNION of team-membership
    // AND RA-anchored projects). ConsultantDayEntryDto for Project
    // rows does not carry projectId, so the seen-names Map used
    // synthetic ids and the drawer sent an invalid projectId to
    // /nb-anchor. Matching by name against nbProjects yields the
    // real id.
    const byName = new Map(nbProjects.map((p) => [p.name, { id: p.id, name: p.name }]));
    (daysData?.days || []).forEach((dd) => {
      (dd.entries || []).forEach((e) => {
        if (e.kind === 'Project' && e.projectName && !byName.has(e.projectName) && e.projectId) {
          byName.set(e.projectName, { id: e.projectId, name: e.projectName });
        }
      });
    });
    const nbProjectsForDay = Array.from(byName.values());
    const editable = !we && !monthLocked; // Weekend / locked → row taps stay dead.
    const emptyMessage = we
      ? 'weekend'
      : future
        ? 'no entries yet — planning ahead is allowed'
        : 'working day without logged hours';
    return (
      <>
        {editable && (
          <div className="mt-addbar" data-testid="mt-addbar">
            <button
              type="button"
              className="mt-btn mt-primary"
              data-testid="mt-add-billable"
              onClick={() => setAddBillState({ date: day.date })}
            >＋ Add billable task</button>
            <button
              type="button"
              className="mt-btn mt-nb-btn"
              data-testid="mt-add-nb"
              disabled={nbProjects.length === 0}
              onClick={() => openNbDrawer(null, nbProjectsForDay, day.date, monthLocked)}
            >＋ Add non-billable task</button>
          </div>
        )}
      <table className="mt-g" data-testid="mt-day">
        <colgroup>
          <col style={{ width: 340 }} />
          <col /><col /><col />
          <col style={{ width: 84 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="mt-l">Task / activity</th>
            <th>Billable</th>
            <th>Non-billable</th>
            <th>Leave / sick</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td className="mt-name" style={{ color: we || future ? '#999' : '#c0392b' }} data-testid="mt-day-empty">
                {emptyMessage}
              </td>
              <td><span className="mt-cell">·</span></td>
              <td><span className="mt-cell">·</span></td>
              <td><span className="mt-cell">·</span></td>
              <td>
                <span className={`mt-cell${we || future ? '' : ' mt-gap'}`}>
                  {we || future ? '·' : '!'}
                </span>
              </td>
            </tr>
          )}
          {rows.map((e, i) => {
            if (e.kind === 'Task') {
              const st = e.workItemStatus === 'Closed' ? 'C' : 'A';
              const bh = e.isBillable ? Number(e.hours) || 0 : 0;
              const nh = e.isBillable ? 0 : Number(e.hours) || 0;
              // MT-S2 targets: TITLE and BILLABLE cell (only when > 0)
              // open the WI drawer via context. NB cell is 0 → dead.
              const openTask = () => openWiDrawer(e.workItemId);
              const isLoading = wiLoadingId === e.workItemId;
              return (
                <tr key={i} data-testid={`mt-day-entry-${i}`}>
                  <td className="mt-name">
                    <span className={`mt-mi ${st}`}>{st}</span>
                    <button
                      type="button"
                      className="mt-title-link"
                      data-testid={`mt-task-title-${e.workItemId}`}
                      onClick={openTask}
                    >
                      WI-{e.workItemId} {e.workItemTitle}
                    </button>
                    <span className="mt-tp"> · {e.projectName}</span>
                    {isLoading && <span style={{ marginLeft: 6, color: '#999' }}>…</span>}
                  </td>
                  <td>
                    <span
                      className={`mt-cell ${bh ? 'mt-c-b mt-click' : ''}`}
                      data-testid={`mt-task-b-${e.workItemId}`}
                      onClick={bh ? openTask : undefined}
                      style={bh ? undefined : { cursor: 'default' }}
                    >{bh ? fmtHours(bh) : '·'}</span>
                  </td>
                  <td><span className={`mt-cell ${nh ? 'mt-c-nb' : ''}`}>{nh ? fmtHours(nh) : '·'}</span></td>
                  <td><span className="mt-cell">·</span></td>
                  <td><span className="mt-cell"><b>{fmtHours(e.hours)}</b></span></td>
                </tr>
              );
            }
            if (e.kind === 'Project') {
              // MT-S2 targets: TITLE and NB cell (only when > 0) open
              // the NB drawer for this row's project + day.
              const openNb = () => openNbDrawer(e, nbProjectsForDay, day.date, monthLocked);
              return (
                <tr key={i} data-testid={`mt-day-entry-${i}`}>
                  <td className="mt-name">
                    <button
                      type="button"
                      className="mt-title-link"
                      data-testid={`mt-nb-title-${i}`}
                      onClick={openNb}
                    >{e.projectName}</button>
                    {e.note && <div className="mt-nbnote">📝 {e.note}</div>}
                  </td>
                  <td><span className="mt-cell">·</span></td>
                  <td>
                    <span
                      className={`mt-cell ${e.hours ? 'mt-c-nb mt-click' : ''}`}
                      data-testid={`mt-nb-cell-${i}`}
                      onClick={e.hours ? openNb : undefined}
                      style={e.hours ? undefined : { cursor: 'default' }}
                    >{e.hours ? fmtHours(e.hours) : '·'}</span>
                  </td>
                  <td><span className="mt-cell">·</span></td>
                  <td><span className="mt-cell"><b>{fmtHours(e.hours)}</b></span></td>
                </tr>
              );
            }
            if (e.kind === 'ShortLeave') {
              // Dead per spec: absences do not open a drawer.
              return (
                <tr key={i} data-testid={`mt-day-entry-${i}`}>
                  <td className="mt-name" data-testid="mt-shortleave-row">
                    <div>Short absence{e.interval ? ` ${e.interval}` : ''}</div>
                    {e.note && <div className="mt-nbnote">📝 {e.note}</div>}
                  </td>
                  <td><span className="mt-cell">·</span></td>
                  <td><span className="mt-cell">·</span></td>
                  <td><span className={`mt-cell ${e.hours ? 'mt-c-lv' : ''}`}>{e.hours ? fmtHours(e.hours) : '·'}</span></td>
                  <td><span className="mt-cell"><b>{fmtHours(e.hours)}</b></span></td>
                </tr>
              );
            }
            // Leave — dead per spec.
            const label = day.type === 'SickLeave' ? 'Sick leave' : 'Paid leave';
            return (
              <tr key={i} data-testid={`mt-day-entry-${i}`}>
                <td className="mt-name" data-testid="mt-leave-row" style={{ color: '#666' }}>{label}</td>
                <td><span className="mt-cell">·</span></td>
                <td><span className="mt-cell">·</span></td>
                <td><span className="mt-cell mt-c-lv">{fmtHours(e.hours)}</span></td>
                <td><span className="mt-cell"><b>{fmtHours(e.hours)}</b></span></td>
              </tr>
            );
          })}
          <tr className="mt-sum">
            <td className="mt-name">Day total</td>
            <td><span className="mt-cell">{s.b ? fmtHours(s.b) : '·'}</span></td>
            <td><span className="mt-cell">{s.nn ? fmtHours(s.nn) : '·'}</span></td>
            <td><span className="mt-cell">{s.lv ? fmtHours(s.lv) : '·'}</span></td>
            <td><span className="mt-cell" data-testid="mt-day-total"><b>{s.tot ? fmtHours(s.tot) : '·'}</b></span></td>
          </tr>
        </tbody>
      </table>
      </>
    );
  };

  // MT-S2 — the "+ Add billable task" drawer. Two-step select
  // (project → sold theme) + title, then create + open WI drawer on
  // the new WI. All errors surface inline in the drawer (spec §3.3).
  const AddBillableDrawer = () => {
    const [projectId, setProjectId] = useState(billableProjects[0]?.id ?? 0);
    const [tasks, setTasks] = useState([]);
    const [taskId, setTaskId] = useState(0);
    const [title, setTitle] = useState('');
    const [errText, setErrText] = useState('');
    const [busy, setBusy] = useState(false);
    // RA-S2 — mandatory role picker. Roles come from the picked
    // task's selectableRoles (task demands or project-wide fallback).
    const [roleId, setRoleId] = useState(0);

    useEffect(() => {
      let cancelled = false;
      if (!projectId) return undefined;
      getDelivery(projectId)
        .then((dto) => {
          if (cancelled) return;
          // WBS-RETIRE — WBS filter dropped; every project task is
          // a costed anchor eligible for the picker.
          const rows = dto?.tasks || [];
          setTasks(rows);
          setTaskId(rows[0]?.id ?? 0);
        })
        .catch((e) => { if (!cancelled) setErrText(readServerError(e)); });
      return () => { cancelled = true; };
    }, [projectId]);

    // RA-S2 — reset role when task changes; auto-pick when there's
    // exactly one selectable role (the user has no meaningful choice).
    const currentTask = tasks.find((t) => t.id === taskId);
    const roleOptions = currentTask?.selectableRoles || [];
    useEffect(() => {
      if (roleOptions.length === 1) setRoleId(roleOptions[0].id);
      else setRoleId(0);
    }, [taskId, roleOptions.length]);

    const create = async () => {
      setErrText('');
      const t = (title || '').trim();
      if (!t) { setErrText('Title is required.'); return; }
      if (!taskId) { setErrText('Sold theme is required.'); return; }
      // RA-S2 — spec §2.1: create blocked without role; error stays
      // inline in the drawer (no toast).
      if (!roleId) { setErrText('Role is required — billable work items must be charged against one of the task\'s sold roles.'); return; }
      setBusy(true);
      try {
        // MT-S3.3 — self-assign even for owners/PMs so the drawer
        // that opens on the fresh WI can log hours immediately.
        const wi = await createWorkItem(projectId, { projectTaskId: taskId, projectRoleId: roleId, title: t, selfAssign: true });
        // Immediately open the freshly-created WI drawer for hours.
        const ctx = await getWorkItemContext(wi.id);
        setAddBillState(null);
        setWiCtx(ctx);
      } catch (e) {
        setErrText(readServerError(e));
      } finally { setBusy(false); }
    };

    return (
      <div className="nb-root" data-testid="mt-add-drawer">
        <div className="nb-ov" onClick={() => setAddBillState(null)} />
        <div className="nb-dr">
          <div className="nb-hd">
            <span className="nb-x" onClick={() => setAddBillState(null)}>✕</span>
            <h3>Add billable task</h3>
          </div>
          <div className="nb-bd">
            <div className="nb-sec">Where does it belong?</div>
            <div className="nb-kv">
              <span className="nb-k">Project</span>
              <span>
                <select
                  data-testid="mt-add-project"
                  value={projectId}
                  onChange={(e) => setProjectId(Number(e.target.value))}
                >
                  {billableProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </span>
              <span className="nb-k">Sold theme</span>
              <span>
                <select
                  data-testid="mt-add-task"
                  value={taskId}
                  onChange={(e) => setTaskId(Number(e.target.value))}
                >
                  {tasks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </span>
              <span className="nb-k">Role</span>
              <span>
                <select
                  data-testid="mt-add-role"
                  value={roleId}
                  onChange={(e) => setRoleId(Number(e.target.value))}
                >
                  <option value={0}>— pick a role —</option>
                  {roleOptions.map((r) => (
                    <option key={r.id} value={r.id}>{r.abbreviation ? `${r.abbreviation} — ${r.roleName}` : r.roleName}</option>
                  ))}
                </select>
              </span>
              <span className="nb-k">Title</span>
              <span>
                <input
                  data-testid="mt-add-title"
                  placeholder="Task title…"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                {errText && <div className="mt-add-err" data-testid="mt-add-err">{errText}</div>}
              </span>
            </div>
            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                className="nb-btn nb-primary"
                data-testid="mt-add-create"
                disabled={busy}
                onClick={create}
              >Create &amp; log hours</button>
            </div>
          </div>
          <div className="nb-ftr">
            <button type="button" className="nb-btn" onClick={() => setAddBillState(null)}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-page">
      <div className="mt-pageh">
        <h1>My Timesheet</h1>
      </div>
      {kpiBand}
      <div className="mt-panel">
        <div className="mt-ph">
          <span className="mt-arr" data-testid="mt-prev" onClick={() => nav(-1)}>◀</span>
          <span className="mt-plabel" data-testid="mt-period-label">{periodLabel(view, anchor)}</span>
          <span className="mt-arr" data-testid="mt-next" onClick={() => nav(1)}>▶</span>
          <span className="mt-today" data-testid="mt-today" onClick={goToday}>Today</span>
          <div className="mt-seg" role="tablist">
            <div className={view === 'm' ? 'on' : ''} data-testid="mt-view-m" onClick={() => switchView('m')}>Month</div>
            <div className={view === 'w' ? 'on' : ''} data-testid="mt-view-w" onClick={() => switchView('w')}>Week</div>
            <div className={view === 'd' ? 'on' : ''} data-testid="mt-view-d" onClick={() => switchView('d')}>Day</div>
          </div>
          {monthLocked && <span className="mt-lock" data-testid="mt-lock-badge">🔒 month locked — read-only</span>}
          <span className="mt-sp" />
        </div>
        <div className="mt-pb">
          {error && <div className="mt-error" data-testid="mt-error">{error}</div>}
          {loading ? (
            <div className="mt-empty" data-testid="mt-loading">Loading…</div>
          ) : (
            <div className={`mt-body${refreshing ? ' mt-refreshing' : ''}`} data-testid="mt-body">
              {view === 'm' && renderMonth()}
              {view === 'w' && renderWeek()}
              {view === 'd' && renderDay()}
            </div>
          )}
        </div>
      </div>

      {/* MT-S2 drawers — mounted at the page root so their overlay
          layers over the entire panel. */}
      {wiCtx && (
        <WorkItemDrawer
          open
          wi={wiCtx.wi}
          task={wiCtx.task}
          team={wiCtx.team}
          caller={wiCtx.caller}
          // MT-S3.3 — mirror DeliveryPage's reload contract.
          // onChanged (take-over / start / close / delete / hours):
          // re-fetch the drawer's context AND refresh matrices so KPI
          // and Day view catch up.
          onChanged={async () => {
            try {
              const fresh = await getWorkItemContext(wiCtx.wi.id);
              setWiCtx(fresh);
            } catch (e) { /* keep last-known state; toast is drawer's own */ }
            reload(fmtIso(anchor));
          }}
          // onWorkItemUpdated (PATCH response) — merge the fresh WI
          // into the currently-open drawer's props in place, so title
          // / planning drafts survive a save race without a full
          // context round-trip.
          onWorkItemUpdated={(fresh) => {
            if (!fresh) return;
            setWiCtx((prev) => prev && prev.wi && prev.wi.id === fresh.id
              ? { ...prev, wi: { ...prev.wi, ...fresh } }
              : prev);
          }}
          onClose={() => { setWiCtx(null); reload(fmtIso(anchor)); }}
        />
      )}
      {nbState && (
        <NonBillableDrawer
          open
          date={nbState.date}
          projects={nbState.projects}
          initialProjectId={nbState.initialProjectId}
          initialHours={nbState.initialHours}
          initialNote={nbState.initialNote}
          locked={nbState.locked}
          onSaved={() => reload(nbState.date)}
          onClose={() => { setNbState(null); reload(nbState.date); }}
        />
      )}
      {addBillState && <AddBillableDrawer />}
    </div>
  );
}
